import { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Alert, Button, Card, CardTitle, ToastContext, TransactionDetailHeader, useFetch } from '@reveni-io/reveni-ui'
import { Divider } from '@reveni-io/reveni-ui'

import LineItemToValidate from '@/common/components/LineItemToValidate/LineItemToValidate'
import ValidationSummary from '@/common/components/ValidationSummary/ValidationSummary'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import { websocketActionsWorkspaces } from '@/common/context/websocketActions/constants'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import useWebsocketActions from '@/common/hooks/useWebsocketActions/useWebsocketActions'
import ExchangeOrderAsDraftSelector from '@/returns/components/ExchangeOrderAsDraftSelector/ExchangeOrderAsDraftSelector'
import IssueRefundSelector from '@/returns/components/IssueRefundSelector/IssueRefundSelector'
import ManageRestock from '@/returns/components/ManageRestock/ManageRestock'
import { statuses } from '@/returns/constants'
import { getSumOfSelectedConcepts } from '@/returns/helpers/helpers'
import { getItemsToRejectAmount, getTransactionStore } from '@/returns/helpers/helpers'
import useIssueExchangeOrderProcessing from '@/returns/hooks/useIssueExchangeOrderProcessing/useIssueExchangeOrderProcessing'
import useIssueRefund from '@/returns/hooks/useIssueRefund/useIssueRefund'
import useRefundMethodExtra from '@/returns/hooks/useRefundMethodExtra/useRefundMethodExtra'
import useRestock from '@/returns/hooks/useRestock/useRestock'
import returnsServices from '@/returns/services/services'
import routes from '@/Router/routes.constants'

import { pendingWebsocketActionTypes } from '../TransactionDetailPage/hooks/useWebsocketTransactionAction/constants'
import { availableRefundTypes } from '../TransactionIssueRefundPage/constants'
import useManualIssueRefund from '../TransactionIssueRefundPage/hooks/useManualIssueRefund'
import EditTransactionAmountDrawer from './EditTransactionAmountDrawer/EditTransactionAmountDrawer'
import useEditTransactionResultDrawer from './hooks/useEditTransactionResultDrawer/useEditTransactionResultDrawer'
import useLineItems from './hooks/useLineItems/useLineItems'
import TransactionValidationPageSkeleton from './TransactionValidationPageSkeleton/TransactionValidationPageSkeleton'
import {
  areAllItemsToValidateInProgress,
  getLineItemsToValidate,
  getPartialValidationPayload,
  getRejectedLineItemIds,
  getRejectionWarningMessage,
} from './helpers'
import * as Styles from './styles'

const TransactionValidationPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { hasPermission } = useUserPermissions()
  const {
    lineItems,
    loading,
    transaction,
    onChangeItemStatus,
    onChangeItemReason,
    onChangeItemNotes,
    onChangeItemImages,
    onDeleteItemImage,
    numberOfNewAcceptedItems,
    numberOfNewRejectedItems,
    numberOfNewRequestedProducts,
    getNewLineItemsToReject,
    getNewLineItemsToAccept,
    hasPendingItems,
    hasAcceptedItems,
    hasSomeItemDelayedStatus,
    changeOriginalTransaction,
    resetTransaction,
  } = useLineItems({
    returnId: id,
    acceptAll: location?.state?.acceptAll,
    rejectAll: location?.state?.rejectAll,
    forReviewAll: location?.state?.forReviewAll,
  })

  const rejectedLineItemIds = useMemo(
    () => getRejectedLineItemIds(getNewLineItemsToReject()),
    [getNewLineItemsToReject]
  )

  const { restock, setRestock, restockLocation, setRestockLocation, restockIsValid, inventoryLocations } = useRestock(
    getTransactionStore(info?.stores, transaction?.store_id),
    transaction?.permissions?.allows_manage_restock,
    info?.userInfo?.id
  )
  const { issueExchangeOrderProcessing, setIssueExchangeOrderProcessing } = useIssueExchangeOrderProcessing(
    getTransactionStore(info?.stores, transaction?.store_id),
    transaction?.permissions?.allows_exchange_order_processing,
    transaction?.is_instant
  )

  const {
    refundMethod,
    setRefundMethod,
    note,
    setNote,
    concepts,
    setConcepts,
    availableRefundMethods,
    getSelectedConcepts,
  } = useManualIssueRefund({
    refundType: availableRefundTypes.refund,
    adjustMentRefundMethods: transaction?.refund_info?.adjustment_refund_methods,
    adjustmentConcepts: transaction?.refund_info?.adjustment_concepts,
    originalRefundMethod: transaction?.refund_info?.refund_method,
    refundMethods: transaction?.refund_info?.refund_methods,
    refundConcepts: transaction?.refund_info?.refund_concepts,
    returnId: id,
    initialSelectedConceptsValue: true,
    rejectedLineItemsAmount: getItemsToRejectAmount(getNewLineItemsToReject()),
    rejectedLineItemIds,
    shippingSummaryAmount: transaction?.summary?.bonuses?.shipping_amount,
    lineItems: transaction?.line_items,
    lineItemGroups: transaction?.line_item_groups,
  })

  const { amount: refundMethodExtraAmount } = useRefundMethodExtra({
    returnId: id,
    amount: getSumOfSelectedConcepts(getSelectedConcepts()),
    refundMethod,
  })

  const getRefundQuantity = () => {
    const selectedConceptsAmount = getSumOfSelectedConcepts(getSelectedConcepts())

    if (refundMethodExtraAmount) {
      return Number(refundMethodExtraAmount) + Number(selectedConceptsAmount)
    }

    return selectedConceptsAmount
  }

  const canEditIssueRefund = hasPermission(userPermissions.editIssueRefund, transaction?.store_id)

  const [issueRefund, setIssueRefund] = useIssueRefund(
    transaction?.permissions?.allows_issue_refund,
    getRefundQuantity(),
    transaction?.issue_refund_at_status,
    canEditIssueRefund
  )
  const { fetchData: returnPartialValidation, loading: validating } = useFetch(
    returnsServices.returnPartialValidation,
    {
      doInitialRequest: false,
    }
  )
  const { setPendingWebsocketAction } = useWebsocketActions(websocketActionsWorkspaces.transactions)

  const {
    showEditTransactionResultDrawer,
    closeEditTransactionResultDrawer,
    canEditTransactionTotal,
    editPendingPaymentMenuItems,
    editRefundMenuItems,
    shouldSendModifications,
    onSaveEditTransactionResult,
  } = useEditTransactionResultDrawer({
    storeId: transaction?.store_id,
    setRefundMethod,
    setNote,
    setConcepts,
    changeOriginalTransaction,
    resetTransaction,
  })

  const isFormValid = () => {
    if (areAllItemsToValidateInProgress(lineItems)) {
      return false
    }

    if (getNewLineItemsToReject().some(item => !item.reject_reason)) {
      return false
    }

    if (numberOfNewAcceptedItems > 0 && transaction?.permissions?.allows_manage_restock) {
      return restockIsValid()
    }

    return true
  }

  const onValidate = async () => {
    const payload = getPartialValidationPayload({
      lineItems: getLineItemsToValidate(lineItems),
      issueRefund,
      restock,
      restockLocation,
      issueExchangeOrderProcessing,
      numberOfNewAcceptedItems,
      concepts,
      note,
      refundMethod,
      totalAmount: getRefundQuantity(),
      shouldSendModifications: shouldSendModifications.current,
    })

    try {
      const response = await returnPartialValidation({ orderId: id, payload })

      setPendingWebsocketAction(pendingWebsocketActionTypes.validateTransaction, response)
      navigate(routes.transactionDetailPage.replace(':id', id))
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onCancel = () => {
    navigate(routes.transactionDetailPage.replace(':id', id))
  }

  const shouldShowErrorMessageForShippedTransactionsWithDelayedItems = () => {
    return numberOfNewRejectedItems > 0 && hasSomeItemDelayedStatus() && transaction.status === statuses.shipped
  }

  const shouldShowErrorMessageForReceivedTransactionsWithDelayedItems = () => {
    return numberOfNewRejectedItems > 0 && hasSomeItemDelayedStatus() && transaction.status === statuses.received
  }

  const shouldShowWarningMessageForTransactionWithAcceptedOrRejectedItemsWithoutDelayedItems = () => {
    return (
      (numberOfNewAcceptedItems > 0 || numberOfNewRejectedItems > 0) &&
      !shouldShowErrorMessageForShippedTransactionsWithDelayedItems() &&
      !shouldShowErrorMessageForReceivedTransactionsWithDelayedItems()
    )
  }

  const shouldShowExchangeOrderAsDraftSelector = () => {
    return (
      (hasAcceptedItems() || getTransactionStore(info?.stores, transaction?.store_id)?.preferences?.allows_upsells) &&
      !hasPendingItems() &&
      transaction?.permissions?.allows_exchange_order_processing
    )
  }

  if (loading || validating) return <TransactionValidationPageSkeleton />

  return (
    <Styles.Container>
      <TransactionDetailHeader
        showArrowsNavigation={false}
        orderNumber={`${t('Validar devolución')} ${transaction?.order?.name}`}
      />
      <Styles.MainContainer>
        <Card showBorder={false}>
          <CardTitle>{t('Productos')}</CardTitle>
          <Styles.ProductsCardContainer>
            {getLineItemsToValidate(lineItems)?.map(item => (
              <LineItemToValidate
                key={item.id}
                lineItem={item}
                onChangeStatus={onChangeItemStatus}
                onChangeReason={onChangeItemReason}
                onChangeNotes={onChangeItemNotes}
                onChangeImages={onChangeItemImages}
                onDeleteImage={onDeleteItemImage}
              />
            ))}
            <Divider />
            <ValidationSummary lineItems={getLineItemsToValidate(lineItems)} />
          </Styles.ProductsCardContainer>
        </Card>
        <Card showBorder={false}>
          <CardTitle>{t('Resumen')}</CardTitle>
          <Styles.SummaryCardContainer>
            <ValidationSummary showTitle lineItems={lineItems} />
            {transaction?.permissions?.allows_manage_restock &&
              numberOfNewAcceptedItems > 0 &&
              hasPermission(userPermissions.restockProducts, transaction?.store_id) && (
                <>
                  <Divider />
                  <ManageRestock
                    value={restock}
                    onChange={value => setRestock(value)}
                    locations={inventoryLocations}
                    selectedLocation={restockLocation}
                    onChangeLocation={e => setRestockLocation(e.target.value)}
                    provider={info?.selectedStore?.provider}
                    storeId={transaction?.store_id}
                  />
                </>
              )}
            {shouldShowExchangeOrderAsDraftSelector() && (
              <>
                <Divider />
                <ExchangeOrderAsDraftSelector
                  refundQuantity={getRefundQuantity()}
                  checked={issueExchangeOrderProcessing}
                  onChange={setIssueExchangeOrderProcessing}
                  currency={transaction?.summary?.currency}
                  requestedProducts={numberOfNewRequestedProducts}
                  menuItems={editPendingPaymentMenuItems}
                />
              </>
            )}
            {!hasPendingItems() && hasAcceptedItems() && (
              <>
                <Divider />
                <IssueRefundSelector
                  refundMethod={refundMethod}
                  refundQuantity={getRefundQuantity()}
                  checked={issueRefund}
                  onChange={value => setIssueRefund(value)}
                  allowIssueRefund={transaction?.permissions?.allows_issue_refund}
                  canEditIssueRefund={canEditIssueRefund}
                  currency={transaction?.summary?.currency}
                  isInstant={transaction?.is_instant}
                  refundStatus={transaction?.summary?.refund_status}
                  exchangeForOrderExternalUrl={transaction?.exchange_for_order?.external_url}
                  menuItems={editRefundMenuItems}
                />
              </>
            )}
            {shouldShowWarningMessageForTransactionWithAcceptedOrRejectedItemsWithoutDelayedItems() && (
              <Alert type="warning">
                {t(getRejectionWarningMessage(transaction?.is_instant, numberOfNewRejectedItems))}
              </Alert>
            )}
            {shouldShowErrorMessageForShippedTransactionsWithDelayedItems() && (
              <Alert type="error">
                {t(
                  'Vas a rechazar productos que el cliente ya ha enviado. Esta acción no se podrá deshacer. ¿Quieres confirmar el rechazo?'
                )}
              </Alert>
            )}
            {shouldShowErrorMessageForReceivedTransactionsWithDelayedItems() && (
              <Alert type="error">
                {t(
                  'Vas a rechazar productos que constan como recibidos. Esta acción no se puede deshacer. ¿Quieres confirmar el rechazo?'
                )}
              </Alert>
            )}
            <Styles.SummaryActionsContainer>
              <Button disabled={!isFormValid()} onClick={onValidate}>
                {t('Confirmar')}
              </Button>
              <Button onClick={onCancel} variant="outlined">
                {t('Cancelar')}
              </Button>
            </Styles.SummaryActionsContainer>
          </Styles.SummaryCardContainer>
        </Card>
      </Styles.MainContainer>
      <EditTransactionAmountDrawer
        show={showEditTransactionResultDrawer}
        concepts={concepts}
        lineItems={getNewLineItemsToAccept()}
        refundMethods={availableRefundMethods}
        refundMethod={refundMethod}
        currency={transaction?.summary?.currency}
        editable={canEditTransactionTotal}
        onClose={closeEditTransactionResultDrawer}
        onSave={onSaveEditTransactionResult}
        returnId={id}
      />
    </Styles.Container>
  )
}

export default TransactionValidationPage
